import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Wallet, Eye, Search, Download } from 'lucide-react';
import { Table, Column } from '../../components/ui/Table';
import { Pagination } from '../../components/ui/Pagination';
import { api } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import { ymToSpanishLabel } from '../../utils/rentPaymentDisplay';

const limit = 20;

type SummaryRow = {
  id: string;
  tenantId: string;
  name: string;
  propertyName: string;
  unitNumber: string;
  monthlyRent: number;
  tenantStatus: string;
  hasArrears: boolean;
  unpaidMonthLabels: string[];
};

export const RentPayments: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [arrearsOnly, setArrearsOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { hasPermission } = usePermissions();
  const canViewPrices = hasPermission('view_prices');

  const { data, isLoading, error } = useQuery({
    queryKey: ['tenant-payments-summary', page, limit, arrearsOnly],
    queryFn: () => api.getTenantPaymentsSummary({ page, limit, arrearsOnly }),
    staleTime: 30_000,
  });

  const filteredRows = useMemo(() => {
    const rows: SummaryRow[] = (data?.data ?? []).map((r) => ({ ...r, id: r.tenantId }));
    if (!searchTerm.trim()) return rows;
    const q = searchTerm.toLowerCase();
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.propertyName.toLowerCase().includes(q) ||
        r.unitNumber.toLowerCase().includes(q),
    );
  }, [data?.data, searchTerm]);

  const columns: Column<SummaryRow>[] = [
    { header: 'Inquilino', accessor: 'name', sortable: true },
    { header: 'Propiedad', accessor: (r) => r.propertyName || '—', sortable: true },
    { header: 'Unidad', accessor: 'unitNumber', sortable: true },
    {
      header: 'Renta',
      accessor: (r) => (canViewPrices ? `$${Number(r.monthlyRent).toLocaleString('es-MX')}` : '—'),
    },
    {
      header: 'Atraso',
      accessor: (r) =>
        r.hasArrears ? (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-900">Sí</span>
        ) : (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">No</span>
        ),
    },
    {
      header: 'Meses sin pago / parcial',
      accessor: (r) =>
        r.unpaidMonthLabels.length > 0 ? (
          <span className="text-sm text-gray-800">
            {r.unpaidMonthLabels.map(ymToSpanishLabel).join(', ')}
          </span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      header: 'Acciones',
      accessor: (row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/rentas/inquilinos/${row.tenantId}?tab=pagos`);
          }}
          className="inline-flex items-center justify-center p-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 text-sm transition-colors"
          title="Ver pagos del inquilino"
          aria-label="Ver pagos del inquilino"
        >
          <Eye className="w-4 h-4" />
        </button>
      ),
    },
  ];

  const totalPages = data?.totalPages ?? 1;
  const total = data?.total ?? 0;

  const exportCsv = () => {
    const rows = filteredRows;
    if (rows.length === 0) return;
    const header = ['Inquilino', 'Propiedad', 'Unidad', 'Renta mensual', 'Atraso', 'Meses sin pago'];
    const body = rows.map((r) => [
      r.name,
      r.propertyName || '',
      r.unitNumber,
      canViewPrices ? String(r.monthlyRent) : '',
      r.hasArrears ? 'Sí' : 'No',
      r.unpaidMonthLabels.map(ymToSpanishLabel).join(' / '),
    ]);
    const csv = [header, ...body].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pagos-renta-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center space-x-3">
          <Wallet className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Pagos</h1>
        </div>
        <button
          type="button"
          onClick={exportCsv}
          disabled={filteredRows.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition-colors"
        >
          <Download className="w-4 h-4" />
          Exportar CSV
        </button>
      </div>

      <p className="text-sm text-gray-600 max-w-3xl">
        Resumen por inquilino: si hay atraso según los registros de cada mes, y en qué meses figura como no pagado o
        pago parcial. El historial detallado y los nuevos registros están en el detalle del inquilino (pestaña Pagos).
      </p>

      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
          <div className="flex-1 relative min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar en esta página…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={arrearsOnly}
              onChange={(e) => {
                setArrearsOnly(e.target.checked);
                setPage(1);
              }}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            Solo inquilinos con atraso
          </label>
        </div>

        {isLoading && <p className="text-gray-500 py-8 text-center">Cargando…</p>}
        {error && (
          <p className="text-red-600 py-4">{(error as Error).message || 'Error al cargar pagos'}</p>
        )}
        {!isLoading && !error && (
          <>
            {filteredRows.length === 0 ? (
              <p className="text-gray-500 py-8 text-center text-sm">No hay inquilinos en esta vista.</p>
            ) : (
              <Table columns={columns} data={filteredRows} />
            )}
            {!searchTerm.trim() && (
              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
              />
            )}
            {searchTerm.trim() && (
              <p className="text-xs text-gray-500">Filtro de texto solo afecta a los resultados de la página actual.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};
